import { useEffect, useRef, useState } from 'react';
import BurstWord from './BurstWord';

type Headline = {
  prefix: string;
  word: string;
  color: string;
  icons: string[];
  particleColors: string[];
  suffix?: string;
};

export default function Hero() {
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const [progress, setProgress] = useState(0);
  const [viewportH, setViewportH] = useState<number>(
    typeof window !== 'undefined' ? window.innerHeight : 800,
  );
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 20;
      const y = (e.clientY / window.innerHeight - 0.5) * 20;
      setMouse({ x, y });
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      const scrolled = Math.min(Math.max(-rect.top, 0), Math.max(total, 1));
      setProgress(total > 0 ? scrolled / total : 0);
    };
    const onResize = () => {
      setViewportH(window.innerHeight);
      onScroll();
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const tapeItems = [
    'BRAND CONNOISSEUR',
    'DESK DECORATOR',
    'CABLE TAMER',
    'REP COUNTER',
    'APPLE AFICIONADO',
    'PIXEL PERFECTIONIST',
    'COFFEE ADDICT',
    'TYPE ENTHUSIAST',
  ];

  const tapeItemsB = [
    'INDIE SPIRIT',
    'DETAIL OBSESSED',
    'NIGHT OWL',
    'IDEA HUNTER',
    'MOTION LOVER',
    'GRID BENDER',
    'COLOR NERD',
    'CRAFT FIRST',
  ];

  const headlines: Headline[] = [
    {
      prefix: 'LOVING',
      word: 'AESTHETICS',
      color: '#ff5a3d',
      icons: ['ri-heart-fill', 'ri-fire-fill', 'ri-shape-2-line', 'ri-brush-3-fill', 'ri-flower-fill', 'ri-magic-fill'],
      particleColors: ['#ff5a3d', '#ffc9d9', '#ffc23c', '#f5f0e6'],
      suffix: '.',
    },
    {
      prefix: 'CRAFTING',
      word: 'STORIES',
      color: '#ffc23c',
      icons: ['ri-book-open-fill', 'ri-sparkling-fill', 'ri-quill-pen-fill', 'ri-star-fill', 'ri-magic-fill', 'ri-shape-2-line'],
      particleColors: ['#ffc23c', '#ff5a3d', '#f5f0e6', '#ffc9d9'],
      suffix: '.',
    },
    {
      prefix: 'SHIPPING',
      word: 'MAGIC',
      color: '#0f8a6a',
      icons: ['ri-rocket-fill', 'ri-flashlight-fill', 'ri-sparkling-2-fill', 'ri-shape-line', 'ri-star-smile-fill', 'ri-fire-fill'],
      particleColors: ['#0f8a6a', '#ffc23c', '#f5f0e6', '#ff5a3d'],
      suffix: '.',
    },
  ];

  const stageCount = headlines.length;
  const getStageOpacity = (i: number) => {
    const center = i / (stageCount - 1);
    const width = 1 / (stageCount - 1);
    const dist = Math.abs(progress - center);
    return Math.max(0, Math.min(1, 1 - dist / width));
  };

  // Tapes start at bottom, slide upward but stop clearly below the navbar (upper third area)
  const tapeTravel = Math.max(viewportH * 0.5, 260);
  const tapeAOffset = -progress * tapeTravel;
  const tapeBOffset = -progress * (tapeTravel + 30); // yellow ends a bit higher than orange

  // Tape rotations: start crossed (X shape), open up into a V/parallel layout as scroll progresses
  const tapeARotation = -4 + progress * 4;   // -4deg -> 0deg
  const tapeBRotation = 5 - progress * 15;   // +5deg -> -10deg (flips to same side as orange, opening the X)

  // Headline drifts downward on scroll — modest amount so it doesn't glue to the bottom
  const headlineDrop = progress * 120;

  // Color interpolation for the tapes across the three headline stages
  const hexToRgb = (hex: string) => {
    const h = hex.replace('#', '');
    return [
      parseInt(h.substring(0, 2), 16),
      parseInt(h.substring(2, 4), 16),
      parseInt(h.substring(4, 6), 16),
    ] as const;
  };
  const rgbToStr = (r: number, g: number, b: number) => `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;
  const mixHex = (a: string, b: string, t: number) => {
    const [r1, g1, b1] = hexToRgb(a);
    const [r2, g2, b2] = hexToRgb(b);
    return rgbToStr(r1 + (r2 - r1) * t, g1 + (g2 - g1) * t, b1 + (b2 - b1) * t);
  };
  const interpPalette = (stops: string[], t: number) => {
    const clamped = Math.max(0, Math.min(1, t));
    const seg = 1 / (stops.length - 1);
    const idx = Math.min(stops.length - 2, Math.floor(clamped / seg));
    const local = (clamped - idx * seg) / seg;
    return mixHex(stops[idx], stops[idx + 1], local);
  };
  // Tape A cycles: orange -> pink-coral -> green
  const tapeAColor = interpPalette(['#ff5a3d', '#ff6f9c', '#0f8a6a'], progress);
  const tapeATextColor = interpPalette(['#ffc9d9', '#fff2e0', '#c7f0d9'], progress);
  // Tape B cycles: yellow -> mint -> soft orange
  const tapeBColor = interpPalette(['#ffc23c', '#7fd5b6', '#ff9a3d'], progress);
  const tapeBTextColor = '#0f0f10';

  return (
    <section
      ref={sectionRef}
      className="relative w-full bg-[#0f0f10]"
      style={{ height: '300vh' }}
    >
      <div className="sticky top-0 h-screen w-full overflow-hidden flex flex-col">
        {/* Background floating blobs */}
        <div
          className="absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full bg-[#ff5a3d]/20 blur-[120px] animate-float-slow pointer-events-none"
          style={{ transform: `translate(${mouse.x}px, ${mouse.y}px)` }}
        />
        <div
          className="absolute top-1/3 -right-32 w-[520px] h-[520px] rounded-full bg-[#ffc23c]/10 blur-[140px] animate-float-slow-2 pointer-events-none"
          style={{ transform: `translate(${-mouse.x}px, ${-mouse.y}px)` }}
        />
        <div
          className="absolute bottom-10 left-1/3 w-[380px] h-[380px] rounded-full bg-[#0f8a6a]/15 blur-[120px] animate-float-slow-3 pointer-events-none"
          style={{ transform: `translate(${mouse.x * 0.6}px, ${-mouse.y * 0.6}px)` }}
        />

        {/* Grain overlay */}
        <div className="absolute inset-0 bg-grain opacity-40 pointer-events-none" />

        {/* Subtle grid lines */}
        <div
          className="absolute inset-0 opacity-[0.12] pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)',
            backgroundSize: '80px 80px',
          }}
        />

        {/* Main content — headline drifts downward on scroll */}
        <div className="relative z-10 flex-1 flex items-center justify-center px-6 md:px-10 pt-24">
          <div
            className="w-full max-w-5xl"
            style={{
              transform: `translateY(${headlineDrop - 60}px)`,
              willChange: 'transform',
            }}
          >
            {/* Small tag above title */}
            <div className="flex items-center justify-center gap-3 mb-6 md:mb-10 animate-rise">
              <span className="w-1.5 h-1.5 rounded-full bg-[#0f8a6a] animate-blink" />
              <span className="text-[10px] md:text-xs tracking-[0.3em] uppercase text-[#f5f0e6]/50 whitespace-nowrap">
                Independent Studio &nbsp;·&nbsp; Est. 2016
              </span>
            </div>

            {/* Headline stack — cross-fade on scroll */}
            <div className="relative h-[120px] sm:h-[180px] md:h-[220px] lg:h-[150px]">
              {headlines.map((h, i) => {
                const opacity = getStageOpacity(i);
                return (
                  <h1
                    key={h.word}
                    className="absolute inset-0 font-display text-center leading-[1] text-[48px] sm:text-7xl md:text-8xl lg:text-[120px] flex items-center justify-center"
                    style={{
                      opacity,
                      pointerEvents: opacity > 0.6 ? 'auto' : 'none',
                      transition: 'opacity 0.2s linear',
                    }}
                  >
                    <span className="block">
                      {h.prefix}{' '}
                      <BurstWord
                        text={h.word}
                        color={h.color}
                        icons={h.icons}
                        particleColors={h.particleColors}
                      />
                      {h.suffix}
                    </span>
                  </h1>
                );
              })}
            </div>

            {/* Hint */}
            <p
              className="mt-10 md:mt-16 text-center text-[11px] md:text-xs tracking-[0.25em] uppercase text-[#f5f0e6]/40 animate-rise delay-400"
              style={{ opacity: 1 - progress * 0.9 }}
            >
              <i className="ri-cursor-fill mr-2 text-[#ff5a3d]" />
              Try tapping the colored words
            </p>
          </div>
        </div>

        {/* Tapes — start at the bottom, animate upward as user scrolls */}
        <div className="pointer-events-none">
          {/* Tape B — yellow, behind */}
          <div
            className="absolute left-1/2 w-[130%] py-2.5 md:py-3 overflow-hidden z-[6]"
            style={{
              bottom: '140px',
              backgroundColor: tapeBColor,
              transform: `translate(-50%, ${tapeBOffset}px) rotate(${tapeBRotation}deg)`,
              willChange: 'transform, background-color',
              transition: 'background-color 0.2s linear',
            }}
          >
            <div className="flex animate-marquee-reverse whitespace-nowrap">
              {[...tapeItemsB, ...tapeItemsB].map((item, i) => (
                <div key={`b-${i}`} className="flex items-center flex-shrink-0">
                  <span
                    className="font-display text-base md:text-xl px-6 tracking-wide"
                    style={{ color: tapeBTextColor }}
                  >
                    {item}
                  </span>
                  <i className="ri-star-fill text-sm md:text-base" style={{ color: tapeBTextColor }} />
                </div>
              ))}
            </div>
          </div>

          {/* Tape A — orange, on top */}
          <div
            className="absolute left-1/2 w-[130%] py-3 md:py-4 overflow-hidden z-[7]"
            style={{
              bottom: '100px',
              backgroundColor: tapeAColor,
              transform: `translate(-50%, ${tapeAOffset}px) rotate(${tapeARotation}deg)`,
              willChange: 'transform, background-color',
              transition: 'background-color 0.2s linear',
            }}
          >
            <div className="flex animate-marquee whitespace-nowrap">
              {[...tapeItems, ...tapeItems].map((item, i) => (
                <div key={i} className="flex items-center flex-shrink-0">
                  <span
                    className="font-display text-lg md:text-2xl px-6 tracking-wide"
                    style={{ color: tapeATextColor }}
                  >
                    {item}
                  </span>
                  <i className="ri-shape-line text-base md:text-lg" style={{ color: tapeATextColor }} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll cue */}
        <div
          className="absolute bottom-3 right-4 md:right-8 z-20 flex flex-col items-center gap-1 text-[#f5f0e6]/50 pointer-events-none"
          style={{ opacity: 1 - progress }}
        >
          <span className="text-[9px] tracking-[0.3em] uppercase" style={{ writingMode: 'vertical-rl' }}>
            Scroll
          </span>
          <span className="w-px h-6 bg-[#f5f0e6]/40 animate-pulse" />
        </div>

        {/* Stage indicator dots */}
        <div className="absolute top-1/2 -translate-y-1/2 right-4 md:right-8 z-20 flex flex-col gap-2">
          {headlines.map((_, i) => {
            const active = getStageOpacity(i) > 0.5;
            return (
              <span
                key={i}
                className="w-1.5 h-1.5 rounded-full transition-all duration-300"
                style={{
                  backgroundColor: active ? '#f5f0e6' : 'rgba(245,240,230,0.25)',
                  transform: active ? 'scale(1.4)' : 'scale(1)',
                }}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
}